import { useState } from 'react';
import { Loader2, FileSearch, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function PennylaneInvoiceStatePanel() {
  const [entityId, setEntityId] = useState('bi_immo');
  const [period, setPeriod] = useState('2026-04');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneInvoiceState({
        entity_id: entityId,
        period,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileSearch className="h-5 w-5 text-indigo-500" />
          Pennylane Invoice State (Live)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only diagnostic: what does the Pennylane account actually contain for a given period.
          Calls the live API. No writes, no finalization.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Entity</label>
            <select
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            >
              <option value="bi_immo">Bonjour Immobilier (bi_immo)</option>
              <option value="afpv">AFPV Invest (afpv)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Period (YYYY-MM)</label>
            <input
              type="text"
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background w-28"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2026-04"
            />
          </div>
          <Button onClick={runDiagnostic} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Run Diagnostic
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-4 mt-4">
            {/* Build commit */}
            {result.build_commit && (
              <div className="text-xs text-muted-foreground">
                BUILD_COMMIT: <code className="font-mono bg-muted px-1 rounded">{result.build_commit}</code>
                {' | '}Checked at: {result.checked_at || '-'}
              </div>
            )}

            {/* Top-level error */}
            {result.error && (
              <div className="border border-red-300 bg-red-50 dark:bg-red-950/30 rounded p-3 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                {result.error}
              </div>
            )}

            {/* Section 1: Account Identity */}
            {result.account_identity && (
              <Section title="1. Account Identity">
                {result.account_identity.error ? (
                  <ErrorBlock message={result.account_identity.error} />
                ) : (
                  <KVTable data={result.account_identity} />
                )}
              </Section>
            )}

            {/* Section 2: Raw List Call */}
            {result.raw_list_call && (
              <Section title="2. Raw List Call">
                {result.raw_list_call.error ? (
                  <ErrorBlock message={result.raw_list_call.error} />
                ) : (
                  <>
                    <KVTable data={{
                      'Query params sent': JSON.stringify(result.raw_list_call.query_params_sent),
                      'HTTP status': result.raw_list_call.http_status,
                      'Payload key': result.raw_list_call.payload_key,
                      'Total count': result.raw_list_call.total_count,
                      'has_more': String(result.raw_list_call.has_more),
                      'Top-level keys': (result.raw_list_call.top_level_keys || []).join(', '),
                    }} />
                    {result.raw_list_call.sample_items?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Sample items (up to 5):</p>
                        <div className="overflow-x-auto">
                          <table className="text-xs w-full border-collapse">
                            <thead>
                              <tr className="bg-muted/50">
                                <th className="px-2 py-1 text-left">ID</th>
                                <th className="px-2 py-1 text-left">Ext Ref</th>
                                <th className="px-2 py-1 text-left">Status</th>
                                <th className="px-2 py-1 text-left">Draft</th>
                                <th className="px-2 py-1 text-left">Customer</th>
                                <th className="px-2 py-1 text-left">Currency</th>
                                <th className="px-2 py-1 text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.raw_list_call.sample_items.map((item, i) => (
                                <tr key={i} className="border-t border-muted">
                                  <td className="px-2 py-1 font-mono">{item.id}</td>
                                  <td className="px-2 py-1">{item.external_reference || '-'}</td>
                                  <td className="px-2 py-1">{item.status || '-'}</td>
                                  <td className="px-2 py-1">
                                    {item.draft ? (
                                      <Badge variant="outline" className="text-[10px]">draft</Badge>
                                    ) : (
                                      <Badge className="bg-green-600 text-[10px]">finalized</Badge>
                                    )}
                                  </td>
                                  <td className="px-2 py-1">{item.customer_name || '-'}</td>
                                  <td className="px-2 py-1">{item.currency}</td>
                                  <td className="px-2 py-1 text-right font-mono">{item.amount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {result.raw_list_call.error_body && (
                      <pre className="text-xs bg-muted/50 p-2 rounded mt-2 overflow-x-auto">
                        {JSON.stringify(result.raw_list_call.error_body, null, 2)}
                      </pre>
                    )}
                  </>
                )}
              </Section>
            )}

            {/* Section 3: Status and Channel Breakdown */}
            {(result.status_breakdown || result.channel_breakdown) && (
              <Section title="3. Status and Channel Breakdown">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">By status:</p>
                    {result.status_breakdown?.error ? (
                      <ErrorBlock message={result.status_breakdown.error} />
                    ) : Object.keys(result.status_breakdown || {}).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No invoices</p>
                    ) : (
                      <table className="text-xs w-full">
                        <tbody>
                          {Object.entries(result.status_breakdown).map(([k, v]) => (
                            <tr key={k} className="border-t border-muted">
                              <td className="px-2 py-1">{k}</td>
                              <td className="px-2 py-1 text-right font-mono">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">By channel:</p>
                    {result.channel_breakdown?.error ? (
                      <ErrorBlock message={result.channel_breakdown.error} />
                    ) : Object.keys(result.channel_breakdown || {}).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No invoices</p>
                    ) : (
                      <table className="text-xs w-full">
                        <tbody>
                          {Object.entries(result.channel_breakdown).map(([k, v]) => (
                            <tr key={k} className={`border-t border-muted ${k === 'booking.com' ? 'bg-blue-50 dark:bg-blue-950/30 font-medium' : ''}`}>
                              <td className="px-2 py-1">{k}</td>
                              <td className="px-2 py-1 text-right font-mono">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </Section>
            )}

            {/* Section 4: Uplisting Drafts Search */}
            {result.uplisting_drafts_search && (
              <Section title="4. Uplisting Drafts (external_reference prefix 'uplisting:')">
                {result.uplisting_drafts_search.error ? (
                  <ErrorBlock message={result.uplisting_drafts_search.error} />
                ) : (
                  <KVTable data={{
                    'HTTP status': result.uplisting_drafts_search.http_status,
                    'Total invoices in period': result.uplisting_drafts_search.total_invoices_in_period,
                    'uplisting: prefix matches': result.uplisting_drafts_search.uplisting_prefix_matches,
                    'Of which: draft': result.uplisting_drafts_search.draft_count_among_matches,
                    'Of which: finalized': result.uplisting_drafts_search.finalized_count_among_matches,
                  }} />
                )}
              </Section>
            )}

            {/* Section 5: Detail Call */}
            {result.detail_call && (
              <Section title="5. Detail Call (eye data source)">
                {result.detail_call.error ? (
                  <ErrorBlock message={result.detail_call.error} />
                ) : result.detail_call.skipped ? (
                  <p className="text-xs text-muted-foreground italic">{result.detail_call.reason}</p>
                ) : (
                  <>
                    <KVTable data={{
                      'Invoice ID tested': result.detail_call.invoice_id_tested,
                      'HTTP status': result.detail_call.http_status,
                      'Body present': result.detail_call.body_present ? 'Yes' : 'No',
                      ...(result.detail_call.body_present ? {
                        'Top-level keys': (result.detail_call.top_level_keys || []).join(', '),
                        'Invoice keys': (result.detail_call.invoice_keys || []).join(', '),
                        'Has invoice_lines': String(result.detail_call.has_invoice_lines),
                        'Line count': result.detail_call.line_count,
                      } : {}),
                    }} />
                    {result.detail_call.error_body && (
                      <pre className="text-xs bg-muted/50 p-2 rounded mt-2 overflow-x-auto">
                        {JSON.stringify(result.detail_call.error_body, null, 2)}
                      </pre>
                    )}
                  </>
                )}
              </Section>
            )}

            {/* Decision Tree */}
            <Section title="Decision Tree">
              <div className="text-xs space-y-2 text-muted-foreground">
                <DecisionItem
                  condition="Card shows drafts (count > 0, status draft) but Invoice List page is empty"
                  conclusion="The read-back parsing fix is not live, or deployed but still parsing wrong. Confirm BUILD_COMMIT, then fix frontend rendering."
                />
                <DecisionItem
                  condition="Card shows 0 for the period"
                  conclusion="Drafts are not in this account or this period. Check Account Identity first. Then verify April generation was run after booking.com mapping was saved."
                />
                <DecisionItem
                  condition="Card shows drafts but no booking.com line in channel breakdown"
                  conclusion="Booking.com drafts were never created. The fix is at creation (re-run April or fix channel-string match), not at list rendering."
                />
                <DecisionItem
                  condition="Detail call returns body but the eye still shows nothing"
                  conclusion="Frontend wiring or scrollIntoView target. Detail call returns empty or errors: the eye has no data to show (data/endpoint problem)."
                />
              </div>
            </Section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div className="border rounded-lg p-3">
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}

function KVTable({ data }) {
  return (
    <table className="text-xs w-full">
      <tbody>
        {Object.entries(data).map(([k, v]) => (
          <tr key={k} className="border-t border-muted">
            <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{k}</td>
            <td className="px-2 py-1 font-mono break-all">{v == null ? '-' : String(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ErrorBlock({ message }) {
  return (
    <div className="border border-red-300 bg-red-50 dark:bg-red-950/30 rounded p-2 text-xs text-red-700 dark:text-red-400">
      <AlertTriangle className="inline h-3 w-3 mr-1" />
      {message}
    </div>
  );
}

function DecisionItem({ condition, conclusion }) {
  return (
    <div className="border-l-2 border-muted-foreground/30 pl-2">
      <p className="font-medium text-foreground">{condition}</p>
      <p>{conclusion}</p>
    </div>
  );
}
